import * as React from 'react';

import { MENU_LABELS, MENU_OPTIONS, READ_ALOUD_SETTING, ReadAloudSetting, SETTING_LABELS, effectiveReadAloudForSetting, onOrOff, settingFor, settingForState } from './read_aloud_setting';

import { STUDENT_EXPERIENCE_SECTION_ID } from '../accounts/edit/section_ids';
import { requestPut } from '../../../../modules/request/index';
import { DataTable, DropdownInput, EXTRA_SMALL_ICON_BASE_SRC, RadioButton } from '../../../Shared';
import useModalAccessibility from '../../../Shared/hooks/useModalAccessibility'
import DemoGuardedButton from '../shared/demoGuardedButton';

const MY_ACCOUNT_STUDENT_EXPERIENCE_PATH = `/teachers/my_account#${STUDENT_EXPERIENCE_SECTION_ID}`

const ICON_SRC = { [READ_ALOUD_SETTING.ON]: `${EXTRA_SMALL_ICON_BASE_SRC}/check-circle.svg`, [READ_ALOUD_SETTING.OFF]: `${EXTRA_SMALL_ICON_BASE_SRC}/clear-enabled-black.svg`, }

const SettingLabel = ({ value, }: { value: ReadAloudSetting }) => (
  <span className="read-aloud-setting-label">
    {value !== READ_ALOUD_SETTING.DEFAULT && <img alt="" className={`read-aloud-setting-icon ${value}`} src={ICON_SRC[value]} />}
    {MENU_LABELS[value]}
  </span>
)
const formatMenuOption = (option: { value: ReadAloudSetting }) => <SettingLabel value={option.value} />
const MENU_SELECT_PROPS = { formatOptionLabel: formatMenuOption, }

const TABLE_HEADERS = [
  { width: '130px', name: 'Student', attribute: 'name', },
  { width: '200px', name: 'Current Setting', attribute: 'setting', noTooltip: true, rowSectionClassName: 'tooltip-section', },
  { width: '130px', name: 'Updated Setting', attribute: 'updated', noTooltip: true, },
]


interface ReadAloudSettingsModalProps {
  classroom: any
  close: () => void
  isOwnedByCurrentUser: boolean
  onSuccess: (message: string) => void
  students: any[]
}

const ReadAloudSettingsModal = ({ classroom, close, isOwnedByCurrentUser, onSuccess, students, }: ReadAloudSettingsModalProps) => {
  const { modalRef, } = useModalAccessibility(close)
  const { owner_default: ownerDefault, owner_name: ownerName, } = classroom.read_aloud
  const [settings, setSettings] = React.useState<Record<number, ReadAloudSetting>>(() => Object.fromEntries(students.map(student => [student.id, settingFor(student.read_aloud_enabled)])))

  const single = students.length === 1 ? students[0] : null
  const changed = students.some(student => settings[student.id] !== settingFor(student.read_aloud_enabled))

  function setSetting(studentId: number, setting: ReadAloudSetting) {
    setSettings(previous => ({ ...previous, [studentId]: setting, }))
  }

  function save() {
    requestPut(`/teachers/classrooms/${classroom.id}/students/read_aloud_settings`, { settings, }, (body) => {
      if (body?.error) { return }

      onSuccess('Read aloud settings saved')
      close()
    })
  }

  function renderGlobalSettingLink() {
    if (!isOwnedByCurrentUser) { return null }

    return <a className="focus-on-light" href={MY_ACCOUNT_STUDENT_EXPERIENCE_PATH}>Change global setting</a>
  }

  function renderDefaultNote() {
    const whom = single ? 'this student' : 'these students'
    const whoseClassrooms = isOwnedByCurrentUser ? 'classrooms you own' : `classrooms ${ownerName} owns`

    return (
      <div className="read-aloud-default-note">
        <p>Read aloud is <strong><u>{onOrOff(ownerDefault).toLowerCase()}</u></strong> by default for all students in {whoseClassrooms}. You can turn it {onOrOff(!ownerDefault).toLowerCase()} for {whom}.</p>
        {renderGlobalSettingLink()}
      </div>
    )
  }

  function renderOption(student, setting: ReadAloudSetting, description: React.ReactNode) {
    function handleClick() { setSetting(student.id, setting) }

    return (
      <div className="read-aloud-option">
        <RadioButton id={`read-aloud-${setting}`} label={SETTING_LABELS[setting]} onClick={handleClick} selected={settings[student.id] === setting} />
        <p className="read-aloud-option-description">{description}</p>
      </div>
    )
  }

  function renderIndividual(student) {
    return (
      <div className="read-aloud-student-settings">
        <h2 id="readAloudStudentTitle">Read aloud for {student.name}</h2>
        <div aria-labelledby="readAloudStudentTitle" className="read-aloud-options" role="radiogroup">
          {renderOption(student, READ_ALOUD_SETTING.ON, `${student.name} can listen to activity prompts and passages read out loud.`)}
          {renderOption(student, READ_ALOUD_SETTING.OFF, `${student.name} won't see the read aloud option, even if the default setting changes.`)}
          {renderOption(student, READ_ALOUD_SETTING.DEFAULT, <React.Fragment>Currently <strong><u>{onOrOff(ownerDefault).toLowerCase()}</u></strong> for all students. {renderGlobalSettingLink()}</React.Fragment>)}
        </div>
      </div>
    )
  }

  function renderMultiple() {
    const rows = students.map(student => {
      function handleChange(option) { setSetting(student.id, option.value) }

      return {
        id: student.id,
        name: student.name,
        setting: <DropdownInput ariaLabel={`Read aloud setting for ${student.name}`} className="read-aloud-setting-menu bordered small focus-on-light" handleChange={handleChange} id={`read-aloud-setting-${student.id}`} options={MENU_OPTIONS} selectProps={MENU_SELECT_PROPS} value={MENU_OPTIONS.find(option => option.value === settings[student.id])} />,
        updated: <SettingLabel value={settingForState(effectiveReadAloudForSetting(settings[student.id], ownerDefault))} />,
      }
    })

    return (
      <div className="read-aloud-student-settings">
        <DataTable className="show-overflow" headers={TABLE_HEADERS} reportingFormat={true} rows={rows} />
        <p>Changes apply only to the selected students.</p>
      </div>
    )
  }

  const subtitle = single ? `${single.name} • ${classroom.name}` : `${classroom.name} • ${students.length} selected students`

  return (
    <div className="modal-container read-aloud-settings-modal-container">
      <div className="modal-background" />
      <div aria-labelledby="readAloudSettingsModalTitle" aria-modal="true" className="read-aloud-settings-modal quill-modal modal-body" ref={modalRef} role="dialog" tabIndex={-1}>
        <div className="read-aloud-settings-header">
          <h3 className="title" id="readAloudSettingsModalTitle">Read Aloud Settings</h3>
          <p className="subtitle">{subtitle}</p>
        </div>
        {renderDefaultNote()}
        {single ? renderIndividual(single) : renderMultiple()}
        <div className="form-buttons">
          <button className="quill-button outlined grey medium focus-on-light" onClick={close} type="button">Cancel</button>
          <DemoGuardedButton className={`quill-button contained green medium focus-on-light ${changed ? '' : 'disabled'}`} disabled={!changed} onClick={save}>{single ? 'Save' : 'Save changes'}</DemoGuardedButton>
        </div>
      </div>
    </div>
  )
}

export default ReadAloudSettingsModal
